import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { formatDate } from "../utils/format";
import { Building2, User, Shield, Monitor, BookOpen, ChevronDown } from "lucide-react";
import toast from "react-hot-toast";

// ─── Role Configuration ────────────────────────────────────────────────────────
const ROLES = [
  { value: "customer",   label: "Customer",   icon: User,      badge: "badge-default",  color: "#6b7280" },
  { value: "business",   label: "Business",   icon: Building2, badge: "badge-info",     color: "#3b82f6" },
  { value: "web_admin",  label: "Web Admin",  icon: Monitor,   badge: "badge-warning",  color: "#f59e0b" },
  { value: "accounts",   label: "Accounts",   icon: BookOpen,  badge: "badge-success",  color: "#10b981" },
  { value: "admin",      label: "Super Admin",icon: Shield,    badge: "badge-danger",   color: "#ef4444" },
];

const getRoleConfig = (role) => ROLES.find(r => r.value === role) || ROLES[0];

// ─── Role Description helper ────────────────────────────────────────────────
const ROLE_DESCRIPTIONS = {
  customer:  "Standard shopper — can browse, buy and track orders.",
  business:  "Marketplace seller — can list products and manage their store.",
  web_admin: "Website administrator — can manage products, categories and content.",
  accounts:  "Accounts manager — can view orders, process payouts and manage finances.",
  admin:     "Super admin — full access to all platform features and settings.",
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [updatingUser, setUpdatingUser] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // { uid, newRole, currentRole, name }

  async function load() {
    const snap = await getDocs(collection(db, "users"));
    setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function toggleBusinessApproval(uid, current) {
    await updateDoc(doc(db, "users", uid), { businessApproved: !current });
    toast.success(current ? "Business access revoked" : "Business approved");
    load();
  }

  async function changeUserRole(uid, newRole) {
    setUpdatingUser(uid);
    setConfirmModal(null);
    try {
      await updateDoc(doc(db, "users", uid), { role: newRole });
      const cfg = getRoleConfig(newRole);
      toast.success(`Role changed to ${cfg.label} ✓`);
      load();
    } catch (err) {
      toast.error("Failed to update role");
      console.error(err);
    } finally {
      setUpdatingUser(null);
    }
  }

  const filtered = users.filter(u => {
    const matchSearch = !search || u.fullName?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === "all" || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  return (
    <div style={{ padding: "40px 48px", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: "var(--text)" }}>User Management</h1>
        <p style={{ color: "var(--text-muted)", fontSize: 15, marginTop: 4 }}>Promote users, manage roles and approve business sellers.</p>
      </div>

      {/* Role legend */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        {ROLES.map(r => {
          const Icon = r.icon;
          return (
            <div key={r.value} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 100, background: "#f3f4f6", fontSize: 12, fontWeight: 500, color: r.color, border: `1px solid ${r.color}22` }}>
              <Icon size={12} />
              {r.label}
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap", alignItems: "center" }}>
        <input className="form-input" placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 320, padding: "12px 16px", borderRadius: 10 }} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", background: "#fff", padding: 6, borderRadius: 12, border: "1px solid var(--border)" }}>
          {["all", ...ROLES.map(r => r.value)].map(r => {
            const config = getRoleConfig(r);
            return (
              <button key={r} onClick={() => setRoleFilter(r)}
                style={{
                  padding: "8px 16px", borderRadius: 8,
                  border: "none",
                  background: roleFilter === r ? (r === "all" ? "var(--text)" : config.color) : "transparent",
                  color: roleFilter === r ? "#fff" : (r === "all" ? "var(--text-muted)" : config.color),
                  fontSize: 13, fontWeight: roleFilter === r ? 600 : 500, cursor: "pointer", transition: "all 0.2s"
                }}
                onMouseEnter={e => { if (roleFilter !== r) e.currentTarget.style.background = r === "all" ? "var(--surface)" : `${config.color}15`; }}
                onMouseLeave={e => { if (roleFilter !== r) e.currentTarget.style.background = "transparent"; }}>
                {r === "all" ? "All Users" : config.label}
              </button>
            )
          })}
        </div>
        <span style={{ marginLeft: "auto", fontSize: 14, fontWeight: 500, color: "var(--text-muted)" }}>
          {filtered.length} user{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="card" style={{ overflow: "hidden", background: "#fff", border: "1px solid var(--border)" }}>
        {loading ? <div style={{ padding: 60, textAlign: "center" }}><div className="loader"><div className="spinner" /></div></div> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: "var(--surface)", borderBottom: "2px solid var(--border)" }}>
                  {["User", "Email", "Current Role", "Phone", "Joined", "Change Role"].map(h => (
                    <th key={h} style={{ padding: "16px 24px", textAlign: "left", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => {
                  const cfg = getRoleConfig(u.role);
                  const Icon = cfg.icon;
                  return (
                    <tr key={u.id} style={{ borderBottom: "1px solid var(--border)", transition: "background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "16px 24px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ width: 40, height: 40, borderRadius: "50%", background: `${cfg.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 16, color: cfg.color, flexShrink: 0 }}>
                            {u.fullName?.[0]?.toUpperCase() || "?"}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: "var(--text)" }}>{u.fullName || "—"}</div>
                            {u.businessName && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{u.businessName}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "16px 24px", color: "var(--text-muted)", fontSize: 13 }}>{u.email}</td>
                      <td style={{ padding: "16px 24px" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 100, background: `${cfg.color}15`, color: cfg.color, fontSize: 12, fontWeight: 700 }}>
                          <Icon size={14} />
                          {cfg.label}
                        </span>
                      </td>
                      <td style={{ padding: "16px 24px", color: "var(--text-muted)", fontSize: 13 }}>{u.phone || "—"}</td>
                      <td style={{ padding: "16px 24px", color: "var(--text-muted)", fontSize: 13 }}>{formatDate(u.createdAt)}</td>
                      <td style={{ padding: "16px 24px" }}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        {/* Role selector */}
                        <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                          <select
                            value={u.role || "customer"}
                            onChange={(e) => {
                              const newRole = e.target.value;
                              if (newRole === u.role) return;
                              const isElevated = ["web_admin", "accounts", "admin"].includes(newRole);
                              if (isElevated) {
                                setConfirmModal({ uid: u.id, newRole, currentRole: u.role, name: u.fullName || u.email });
                              } else {
                                changeUserRole(u.id, newRole);
                              }
                            }}
                            disabled={updatingUser === u.id}
                            style={{
                              padding: "7px 32px 7px 10px", borderRadius: 8,
                              border: `1.5px solid ${cfg.color}55`,
                              background: `${cfg.color}0d`,
                              color: cfg.color,
                              fontSize: 12, fontWeight: 600,
                              cursor: updatingUser === u.id ? "not-allowed" : "pointer",
                              opacity: updatingUser === u.id ? 0.6 : 1,
                              appearance: "none", WebkitAppearance: "none",
                              outline: "none",
                            }}>
                            {ROLES.map(r => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                          <ChevronDown size={12} style={{ position: "absolute", right: 8, pointerEvents: "none", color: cfg.color }} />
                        </div>

                        {/* Business approve/revoke */}
                        {u.role === "business" && (
                          <button onClick={() => toggleBusinessApproval(u.id, u.businessApproved)}
                            className="btn btn-sm"
                            style={{ background: u.businessApproved ? "var(--danger)" : "var(--success)", color: "#fff", border: "none", fontSize: 11, padding: "5px 10px", borderRadius: 6 }}>
                            {u.businessApproved ? "Revoke" : "Approve"}
                          </button>
                        )}

                        {updatingUser === u.id && (
                          <div style={{ width: 16, height: 16, border: "2px solid var(--border)", borderTopColor: "var(--brand)", borderRadius: "50%", animation: "spin 0.6s linear infinite" }} />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <p style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>No users found.</p>
        )}
      </div>

      {/* Confirmation Modal for elevated roles */}
      {confirmModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div className="card" style={{ padding: 32, maxWidth: 440, width: "90%", textAlign: "center" }}>
            {(() => {
              const cfg = getRoleConfig(confirmModal.newRole);
              const Icon = cfg.icon;
              return (
                <>
                  <div style={{ width: 56, height: 56, borderRadius: "50%", background: `${cfg.color}18`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                    <Icon size={24} color={cfg.color} />
                  </div>
                  <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 18, marginBottom: 10 }}>
                    Promote to {cfg.label}?
                  </h3>
                  <p style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>
                    You are about to give <strong>{confirmModal.name}</strong> the <strong style={{ color: cfg.color }}>{cfg.label}</strong> role.
                  </p>
                  <p style={{ color: "var(--text-muted)", fontSize: 13, lineHeight: 1.6, marginBottom: 24, padding: "10px 16px", background: `${cfg.color}0d`, borderRadius: 8, border: `1px solid ${cfg.color}22` }}>
                    {ROLE_DESCRIPTIONS[confirmModal.newRole]}
                  </p>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={() => setConfirmModal(null)} className="btn btn-secondary" style={{ flex: 1 }}>
                      Cancel
                    </button>
                    <button
                      onClick={() => changeUserRole(confirmModal.uid, confirmModal.newRole)}
                      className="btn btn-primary"
                      style={{ flex: 1, background: cfg.color, borderColor: cfg.color }}>
                      Confirm Promotion
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
